import { Icon, type IconDefinition } from '@vrplatform/icons';
import type { Maybe } from '@vrplatform/utils';
import {
  Box,
  Center,
  Group,
  Modal,
  Skeleton,
  Stack,
  Text,
  VisuallyHidden,
  rem,
} from '@mantine/core';
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useState,
} from 'react';
import { Button } from '../button';

type Props =
  | {
      secondConfirm: {
        title: string;
        subtitle: string;
        submitTitle?: string;
      };
      onSubmit: (
        isSecondConfirm: boolean
        // biome-ignore lint/suspicious/noConfusingVoidType: <explanation>
      ) => Promise<{ showConfirm: boolean } | void>;
    }
  | {
      secondConfirm?: undefined;
      onSubmit: () => void;
    };

type FirstProps = {
  opened: boolean;
  closeModal: () => void;
  loading: boolean;
  validation?: ConfirmModalValidation;
  validationMessageClassName?: string;
  errorMessage?: string | null;
  preventClose?: boolean;
  loadingSubmitTitle?: string;
  submitTitle?: string;
  submitIcon?: IconDefinition;
  title: string;
  subtitle: string;
  /** Extra content between the subtitle and the buttons, such as a required acknowledgement. */
  children?: ReactNode;
  /** Keeps submit disabled, for example until an acknowledgement is checked. */
  submitDisabled?: boolean;
  entityTitle?: Maybe<string>;
  type: 'confirm' | 'delete' | 'warning';
  icon?: IconDefinition;
  size?: ComponentProps<typeof Modal>['size'];
  zIndex?: ComponentProps<typeof Modal>['zIndex'];
  withinPortal?: ComponentProps<typeof Modal>['withinPortal'];
} & Props;

type ConfirmModalValidation = {
  isLoading: boolean;
  data: unknown;
  error: unknown;
};

export const ConfirmModal = (props: FirstProps) => {
  const [confirmOpened, setConfirmOpened] = useState<boolean>(false);
  const { closeModal } = props;

  const closeSecond = useCallback(() => {
    setConfirmOpened(false);
    closeModal();
  }, [closeModal]);

  const openSecond = useCallback(() => {
    setConfirmOpened(true);
  }, []);

  return (
    <>
      <First
        {...props}
        opened={props.opened && !confirmOpened}
        openConfirm={openSecond}
      />
      {props.secondConfirm && (
        <Second
          {...props}
          onSubmit={props.onSubmit}
          opened={confirmOpened}
          closeModal={closeSecond}
          title={props.secondConfirm.title}
          subtitle={props.secondConfirm.subtitle}
          submitTitle={props.secondConfirm.submitTitle}
        />
      )}
    </>
  );
};

const First = ({
  closeModal,
  opened,
  loading,
  onSubmit,
  submitTitle,
  title,
  entityTitle,
  subtitle,
  children,
  submitDisabled = false,
  type,
  icon,
  submitIcon,
  size = 380,
  zIndex,
  secondConfirm,
  validation,
  validationMessageClassName,
  errorMessage,
  preventClose = false,
  loadingSubmitTitle,
  openConfirm,
  withinPortal,
}: FirstProps & {
  openConfirm: () => void;
}) => {
  const isSecondConfirm = !!secondConfirm;
  const validationReady = validation
    ? !!validation.data && !validation.error && !validation.isLoading
    : true;

  const submit = useCallback(async () => {
    if (isSecondConfirm) {
      await onSubmit(false).then((result) => {
        if (result?.showConfirm) {
          openConfirm();
        }
      });
    } else {
      onSubmit();
    }
  }, [isSecondConfirm, onSubmit, openConfirm]);

  return (
    <Modal
      opened={opened}
      onClose={preventClose ? () => undefined : closeModal}
      closeOnClickOutside={!preventClose}
      closeOnEscape={!preventClose}
      withCloseButton={!preventClose}
      closeButtonProps={{ 'aria-label': 'Close dialog' }}
      title={<VisuallyHidden>{title}</VisuallyHidden>}
      size={size}
      radius="md"
      centered
      withinPortal={withinPortal}
      zIndex={zIndex}
      styles={{
        body: {
          paddingInline: rem(30),
          paddingBottom: rem(30),
        },
        header: {
          paddingBottom: 0,
          minHeight: 0,
        },
      }}
    >
      <Box
        mb={'xl'}
        sx={() => ({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        })}
      >
        <Icon
          icon={icon || (type === 'confirm' ? 'CheckCircleIcon' : 'TrashIcon')}
          color={({ colors, primaryColor }) =>
            colors[
              type === 'confirm'
                ? primaryColor
                : type === 'delete'
                  ? 'red'
                  : 'orange'
            ][6]
          }
          size={30}
        />
        <Text fw={500} component="h3" ta="center" size="lg" mt="xl" mb="md">
          {title}{' '}
          {entityTitle && (
            <>
              <br />
              {entityTitle}
            </>
          )}
        </Text>

        <Text component="p" c="neutral" ta="center" size="sm">
          {subtitle}
        </Text>
      </Box>

      {children}
      {validation && (
        <ConfirmModalValidationBanner
          messageClassName={validationMessageClassName}
          validation={validation}
        />
      )}
      {errorMessage && (
        <ConfirmModalValidationPanel
          tone="error"
          title="Operation failed"
          message={errorMessage}
          messageClassName={validationMessageClassName}
        />
      )}

      <Group wrap="nowrap">
        <Button
          sx={{
            width: 100,
          }}
          onClick={closeModal}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={submit}
          leftIcon={submitIcon}
          variant={type === 'confirm' ? 'primary' : 'filled'}
          loading={loading}
          color={
            type === 'confirm'
              ? undefined
              : type === 'delete'
                ? 'red'
                : 'orange'
          }
          disabled={!validationReady || submitDisabled}
          sx={{
            flex: 1,
            fontWeight: type === 'confirm' ? undefined : 500,
          }}
        >
          {loading && loadingSubmitTitle
            ? loadingSubmitTitle
            : submitTitle ||
              (type === 'confirm' ? 'Yes, submit' : 'Yes, delete')}
        </Button>
      </Group>
    </Modal>
  );
};

type ConfirmModalValidationPanelTone = 'info' | 'success' | 'error';

const CONFIRM_MODAL_VALIDATION_PANEL_ICON = {
  error: 'CrossCircleIcon',
  info: 'InfoIcon',
  success: 'CheckCircleIcon',
} as const;

const CONFIRM_MODAL_VALIDATION_PANEL_COLORS = {
  error: {
    background: 'red',
    border: 'red',
    foreground: 'red',
  },
  info: {
    background: 'blue',
    border: 'blue',
    foreground: 'blue',
  },
  success: {
    background: 'green',
    border: 'green',
    foreground: 'green',
  },
} as const;

const DEFAULT_VALIDATION_ERROR_MESSAGE =
  'This action cannot be completed. Please contact support if the issue persists.';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getMessageFromRecord = (value: unknown) => {
  if (!isRecord(value)) return undefined;

  return typeof value.message === 'string' && value.message
    ? value.message
    : undefined;
};

const getValidationErrorMessage = (error: unknown) => {
  if (!isRecord(error)) return DEFAULT_VALIDATION_ERROR_MESSAGE;

  const issues = error.issues;
  if (Array.isArray(issues)) {
    const issueMessage = issues.map(getMessageFromRecord).find(Boolean);
    if (issueMessage) return issueMessage;
  }

  const errorValue = error.error;
  if (Array.isArray(errorValue)) {
    const errorMessage = errorValue.map(getMessageFromRecord).find(Boolean);
    if (errorMessage) return errorMessage;
  }

  return (
    getMessageFromRecord(errorValue) ||
    getMessageFromRecord(error) ||
    DEFAULT_VALIDATION_ERROR_MESSAGE
  );
};

const ConfirmModalValidationPanel = ({
  message,
  title,
  tone,
  messageClassName,
}: {
  message: string;
  messageClassName?: string;
  title: string;
  tone: ConfirmModalValidationPanelTone;
}) => {
  const colors = CONFIRM_MODAL_VALIDATION_PANEL_COLORS[tone];

  return (
    <Box
      mb="xl"
      sx={(theme) => ({
        backgroundColor: theme.colors[colors.background][0],
        border: `1px solid ${theme.colors[colors.border][2]}`,
        borderRadius: theme.radius.md,
        padding: theme.spacing.md,
      })}
    >
      <Group align="flex-start" gap="sm" wrap="nowrap">
        <Center mt={2}>
          <Icon
            icon={CONFIRM_MODAL_VALIDATION_PANEL_ICON[tone]}
            color={(theme) => theme.colors[colors.foreground][7]}
            size={17}
          />
        </Center>
        <Box>
          <Text fw={700} c={colors.foreground} size="sm">
            {title}
          </Text>
          <Text className={messageClassName} size="sm" c="neutral" mt={4}>
            {message}
          </Text>
        </Box>
      </Group>
    </Box>
  );
};

const ConfirmModalValidationBanner = ({
  messageClassName,
  validation,
}: {
  messageClassName?: string;
  validation: ConfirmModalValidation;
}) => {
  if (validation.isLoading) {
    return (
      <Stack mb="xl">
        <Skeleton height={30} radius="md" />
        <Skeleton height={30} radius="md" />
      </Stack>
    );
  }

  if (validation.error) {
    return (
      <ConfirmModalValidationPanel
        tone="error"
        title="This action is blocked"
        message={getValidationErrorMessage(validation.error)}
        messageClassName={messageClassName}
      />
    );
  }

  if (!validation.data) {
    return (
      <ConfirmModalValidationPanel
        tone="info"
        title="Validating action"
        message="Validation must complete before you can continue."
        messageClassName={messageClassName}
      />
    );
  }

  return (
    <ConfirmModalValidationPanel
      tone="success"
      title="Ready to continue"
      message="This action passed validation."
    />
  );
};

const Second = ({
  closeModal,
  opened,
  loading,
  onSubmit,
  submitTitle,
  loadingSubmitTitle,
  preventClose = false,
  subtitle,
  title,
}: {
  opened: boolean;
  closeModal: () => void;
  loading: boolean;
  onSubmit: FirstProps['onSubmit'];
  title: string;
  subtitle: string;
  submitTitle: string | undefined;
  loadingSubmitTitle?: string;
  preventClose?: boolean;
}) => {
  return (
    <Modal
      radius="md"
      opened={opened}
      onClose={preventClose ? () => undefined : closeModal}
      closeOnClickOutside={!preventClose}
      closeOnEscape={!preventClose}
      withCloseButton={!preventClose}
      closeButtonProps={{ 'aria-label': 'Close dialog' }}
      title={<VisuallyHidden>{title}</VisuallyHidden>}
      centered
      size={500}
      styles={(theme) => ({
        body: {
          paddingBottom: theme.spacing.xl,
          paddingInline: theme.spacing.xl,
        },
      })}
    >
      <Icon
        icon="AlertTriangleIcon"
        color={(theme) => theme.colors.orange[5]}
        size={32}
      />
      <Text ta="center" component="h4" size="lg" fw={500} my="lg">
        {title}
      </Text>
      <Text ta="center" component="p" c="neutral" mb="xl">
        {subtitle}
      </Text>

      <Group wrap="nowrap">
        <Button type="button" onClick={closeModal} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          sx={{ flex: 1 }}
          onClick={async () => {
            await onSubmit(true);
            closeModal();
          }}
          loading={loading}
        >
          {loading && loadingSubmitTitle
            ? loadingSubmitTitle
            : submitTitle || 'Yes, submit'}
        </Button>
      </Group>
    </Modal>
  );
};
